import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Users,
  ClipboardCheck,
  Wallet,
  TrendingUp,
  ArrowRight,
  Building2,
  UserCircle,
} from 'lucide-react';
import { HeroIllustration } from '@/components/illustrations/HeroIllustration';
import { WaveDivider } from '@/components/illustrations/WaveDivider';
import { MiniBarChart } from '@/components/illustrations/MiniBarChart';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Home() {
  const { t } = useTranslation();

  const orgFeatures = t('public.home.orgs.features', { returnObjects: true }) as string[];
  const memberFeatures = t('public.home.members.features', { returnObjects: true }) as string[];

  const { ref: featuresRef, isVisible: featuresVisible } = useScrollReveal();
  const { ref: orgsRef, isVisible: orgsVisible } = useScrollReveal();
  const { ref: membersRef, isVisible: membersVisible } = useScrollReveal();

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-primary/10 via-background to-background py-16 sm:py-24 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
              <h1 className="opacity-0 animate-fade-up text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-foreground mb-6">
                {t('public.home.hero.title')}
              </h1>
              <p className="opacity-0 animate-fade-up-delay-1 text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto lg:mx-0">
                {t('public.home.hero.subtitle')}
              </p>
              <div className="opacity-0 animate-fade-up-delay-2 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/org-signup">
                  <Button size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-base px-8">
                    <Building2 className="mr-2 h-5 w-5" />
                    {t('public.home.hero.cta.orgs')}
                  </Button>
                </Link>
                <Link to="/member-login">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto text-base px-8">
                    <UserCircle className="mr-2 h-5 w-5" />
                    {t('public.home.hero.cta.members')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="animate-float">
                <HeroIllustration />
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/5 blur-3xl" />
        </div>
      </section>

      <h2 className="text-center text-2xl font-bold text-gray-800 dark:text-white my-8">{t('public.home.tagline')}</h2>

      <WaveDivider />

      {/* Features Grid */}
      <div
        ref={featuresRef}
        className={`transition-all duration-700 ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <section className="py-16 sm:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
              <Card className="group border-border/50 hover:border-primary/50 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                <CardContent className="p-4 sm:p-6 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-200">
                    <Users className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">{t('members.title')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('dashboard.totalMembers')}
                  </p>
                </CardContent>
              </Card>

              <Card className="group border-border/50 hover:border-primary/50 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                <CardContent className="p-4 sm:p-6 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-200">
                    <ClipboardCheck className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">{t('attendance.title')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('attendance.takeAttendance')}
                  </p>
                </CardContent>
              </Card>

              <Card className="group border-border/50 hover:border-primary/50 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                <CardContent className="p-4 sm:p-6 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-200">
                    <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-blue-500" />
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">{t('dues.title')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('dues.collectDues')}
                  </p>
                </CardContent>
              </Card>

              <Card className="group border-border/50 hover:border-primary/50 hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                <CardContent className="p-4 sm:p-6 text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-200">
                    <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />
                  </div>
                  <h3 className="font-semibold text-sm sm:text-base mb-1 sm:mb-2">{t('finances.title')}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('finances.financialSummary')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>

      <WaveDivider flip={true} />

      {/* For Organizations Section */}
      <div
        ref={orgsRef}
        className={`transition-all duration-700 ${orgsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <section className="py-16 sm:py-20 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                  <Building2 className="h-4 w-4" />
                  <span>{t('public.home.orgs.title')}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                  {t('public.home.orgs.title')}
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg mb-6">
                  {t('public.home.orgs.description')}
                </p>
                <ul className="space-y-3 mb-8">
                  {orgFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/org-signup">
                  <Button className="bg-primary hover:bg-primary/90">
                    {t('public.home.orgs.cta')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-br from-primary/20 to-primary/5 rounded-2xl p-6 sm:p-8">
                  <div className="bg-background rounded-xl shadow-lg p-4 sm:p-6 space-y-4">
                    <div className="flex items-center justify-between pb-4 border-b">
                      <span className="font-semibold">{t('dashboard.quickStats')}</span>
                      <span className="text-xs text-muted-foreground">{t('finances.thisMonth')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-primary/5 rounded-lg p-4">
                        <p className="text-2xl font-bold text-primary">128</p>
                        <p className="text-xs text-muted-foreground">{t('dashboard.totalMembers')}</p>
                      </div>
                      <div className="bg-green-500/5 rounded-lg p-4">
                        <p className="text-2xl font-bold text-green-500">95%</p>
                        <p className="text-xs text-muted-foreground">{t('attendance.attendanceRate')}</p>
                      </div>
                      <div className="bg-blue-500/5 rounded-lg p-4">
                        <p className="text-2xl font-bold text-blue-500">$12.5k</p>
                        <p className="text-xs text-muted-foreground">{t('dues.totalCollected')}</p>
                      </div>
                      <div className="bg-purple-500/5 rounded-lg p-4">
                        <p className="text-2xl font-bold text-purple-500">$3.2k</p>
                        <p className="text-xs text-muted-foreground">{t('donations.title')}</p>
                      </div>
                    </div>
                    <MiniBarChart />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <WaveDivider />

      {/* For Members Section */}
      <div
        ref={membersRef}
        className={`transition-all duration-700 ${membersVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <section className="py-16 sm:py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-2xl p-6 sm:p-8">
                  <div className="bg-background rounded-xl shadow-lg p-4 sm:p-6 space-y-4">
                    <div className="flex items-center space-x-4 pb-4 border-b">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <UserCircle className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">John Doe</p>
                        <p className="text-xs text-muted-foreground">{t('memberPortal.memberSince')} 2023</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-green-500/5 rounded-lg">
                        <span className="text-sm">{t('memberPortal.paymentStatus')}</span>
                        <span className="text-sm font-medium text-green-500">{t('memberPortal.upToDate')}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-primary/5 rounded-lg">
                        <span className="text-sm">{t('memberPortal.myAttendance')}</span>
                        <span className="text-sm font-medium">24/26</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-blue-500/5 rounded-lg">
                        <span className="text-sm">{t('memberPortal.myDues')}</span>
                        <span className="text-sm font-medium">$150 {t('memberPortal.totalPaid')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center space-x-2 bg-green-500/10 text-green-600 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
                  <UserCircle className="h-4 w-4" />
                  <span>{t('public.home.members.title')}</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                  {t('public.home.members.title')}
                </h2>
                <p className="text-muted-foreground text-base sm:text-lg mb-6">
                  {t('public.home.members.description')}
                </p>
                <ul className="space-y-3 mb-8">
                  {memberFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/member-login">
                  <Button variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
                    {t('public.home.members.cta')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary-foreground mb-4">
            {t('public.home.hero.title')}
          </h2>
          <p className="text-primary-foreground/80 text-base sm:text-lg mb-8 max-w-2xl mx-auto">
            {t('public.home.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/org-signup">
              <Button size="lg" variant="secondary" className="relative overflow-hidden w-full sm:w-auto">
                <span className="pointer-events-none absolute inset-0 z-0 bg-gradient-to-r from-transparent via-white/20 to-transparent bg-[length:200%_100%] animate-shimmer" aria-hidden="true" />
                <span className="relative z-10">{t('public.home.orgs.cta')}</span>
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button size="lg" className="w-full sm:w-auto bg-white text-primary hover:bg-white/90 text-base px-8">
                {t('public.nav.howItWorks')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
